/**
 * useAdminSupportTickets — Admin-Ticketverwaltung
 *
 * GET  /api/v2/admin/support_tickets?status=&page=&per_page=
 * GET  /api/v2/admin/support_tickets/:id
 * POST /api/v2/admin/support_tickets/:id/reply
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

export type AdminSupportTicketStatus = 'offen' | 'geschlossen'

export type AdminSupportTicket = {
  id: number
  subject: string
  status: AdminSupportTicketStatus
  user_email: string
  created_at: string
  updated_at: string
}

export type AdminSupportTicketMessage = {
  id: number
  sender: 'user' | 'admin'
  body: string
  created_at: string
}

export type AdminSupportTicketDetail = AdminSupportTicket & {
  messages: AdminSupportTicketMessage[]
}

export type AdminSupportTicketsPagy = {
  page: number
  pages: number
  count: number
  items: number
}

export function useAdminSupportTickets(status: AdminSupportTicketStatus | undefined, page: number) {
  const query = useQuery({
    queryKey: ['admin-support-tickets', status, page],
    queryFn: async () => {
      const result = await apiClient.GET('/api/v2/admin/support_tickets', {
        params: { query: { status, page, per_page: 25 } },
      })
      if (result.error) throw result.error
      return result.data as { data: AdminSupportTicket[]; meta: { pagy: AdminSupportTicketsPagy } }
    },
  })
  return {
    tickets: query.data?.data,
    pagy: query.data?.meta?.pagy,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export function useAdminSupportTicket(id: number | undefined) {
  const query = useQuery({
    queryKey: ['admin-support-tickets', id],
    queryFn: async () => {
      const result = await apiClient.GET('/api/v2/admin/support_tickets/{id}', { params: { path: { id: id! } } })
      if (result.error) throw result.error
      return result.data as AdminSupportTicketDetail
    },
    enabled: id != null,
  })
  return { ticket: query.data, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch }
}

export function useAdminReplySupportTicket(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ message, close }: { message: string; close: boolean }) => {
      const result = await apiClient.POST('/api/v2/admin/support_tickets/{id}/reply', {
        params: { path: { id } },
        body: { message, close },
      })
      if (result.error) throw result.error
      return result.data as AdminSupportTicketDetail
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['admin-support-tickets', id], data)
      queryClient.invalidateQueries({ queryKey: ['admin-support-tickets'] })
    },
  })
}
